import React, { useEffect } from "react";
import Title from "../../common/components/view/ViewTitle";
import View from "../../common/components/view/View";
import ViewHeader from "../../common/components/view/ViewHeader";
import { useParams, useHistory } from "react-router";
import {
  useLivestockWeanQuery,
  useSaveLivestockWeanMutation,
  usePostLivestockWeanMutation,
  useAutoPostItemMutation
} from "../graphql";
import { useFlash } from "../../common/contexts/flash";
import Form from "../../common/components/form/Form";
import FormSubmit from "../../common/components/form/FormSubmit";
import { OnSubmit, useForm } from "react-hook-form";
import Button from "../../common/components/input/Button";
import BackButton from "../../common/components/view/BackButton";
import ViewContent from "../../common/components/view/ViewContent";
import CommentsField from "../components/CommentsField";
import InventoryField from "../components/InventoryField";
import TotalWeightField from "../components/TotalWeightField";
import JobField from "../components/JobField";
import QuantityAndSmallsField from "../components/QuantityAndSmallsField";
import HorizontalSpacer from "../../common/components/layout/HorizontalSpacer";
import FormFieldLabel from "../../common/components/form/FormFieldLabel";
import FormField from "../../common/components/form/FormField";
import FormFieldInput from "../../common/components/form/FormFieldInput";
import FormFieldErrors from "../../common/components/form/FormFieldErrors";
import TypeaheadInput from "../../common/components/input/TypeaheadInput";
import DateInput from "../../common/components/input/DateInput";
import { ErrorCode } from "../../common/utils";

interface FormData {
  event: string;
  postingDate: string;
  quantity: number;
  smallLivestockQuantity?: number;
  totalWeight: number;
  comments?: string;
}

interface ViewParams {
  job: string;
}

const ActivityWeanView: React.FC = () => {
  const history = useHistory();
  const params = useParams<ViewParams>();

  const formContext = useForm<FormData>();
  const { loading, data } = useLivestockWeanQuery({
    variables: {
      job: params.job
    },
    onCompleted({ livestockWean, livestockWeanEventTypes }) {
      const { setValue } = formContext;
      if (livestockWeanEventTypes.length === 1) {
        setValue("event", livestockWeanEventTypes[0].code);
      } else if (livestockWean.event)
        setValue("event", livestockWean.event.code);
      if (livestockWean.quantity) setValue("quantity", livestockWean.quantity);
      if (livestockWean.totalWeight)
        setValue("totalWeight", livestockWean.totalWeight);
      if (livestockWean.postingDate)
        setValue("postingDate", livestockWean.postingDate);
      if (livestockWean.comments) setValue("comments", livestockWean.comments);
    }
  });
  const [post] = usePostLivestockWeanMutation();
  const [save] = useSaveLivestockWeanMutation();
  const { setMessage } = useFlash();
  const { getValues, watch, triggerValidation, formState } = formContext;
  const [autoPostItem] = useAutoPostItemMutation();

  const TEMPLATE_NAME = "WEAN";
  const quantity = watch("quantity") || 0;

  const onSubmit: OnSubmit<FormData> = async data => {
    try {
      await post({
        variables: {
          input: {
            ...data,
            job: params.job
          }
        }
      });
      try {
        await autoPostItem({
          variables: {
            input: {
              itemJournalTemplate: TEMPLATE_NAME
            }
          }
        });
        setMessage({
          message: "Entry recorded successfully.",
          level: "success",
          timeout: 2000
        });
      } catch (e) {
        setMessage({
          message: "Entry was recorded but Posting Failed.",
          level: "error",
          timeout: 2000
        });
      }
      history.push("/livestock-activity");
    } catch (e) {
      const error = e as any;
      if (error.message === ErrorCode.NoAuthAvailable) {
        history.push("/noauth", { modal: true });
      } else {
        setMessage({
          message: error.toString(),
          level: "error",
          timeout: 3000
        });
      }
    }
  };

  const onSave = async () => {
    try {
      await save({
        variables: {
          input: {
            ...getValues(),
            job: params.job
          }
        }
      });
      setMessage({
        message: "Entry saved successfully.",
        level: "success",
        timeout: 2000
      });
      history.push("/livestock-activity");
    } catch (e) {
      const error: any = e;
      setMessage({
        message: error.toString(),
        level: "error"
      });
    }
  };

  // Validate small livestock quantity if total quantity changes.
  useEffect(() => {
    if (formState.isSubmitted) {
      triggerValidation("smallLivestockQuantity");
    }
  }, [triggerValidation, quantity, formState.isSubmitted]);

  return (
    <View>
      <ViewHeader>
        <BackButton />
        <Title>Wean</Title>
      </ViewHeader>
      <ViewContent loading={loading}>
        {data && (
          <Form context={formContext} onSubmit={onSubmit}>
            <JobField
              number={data.livestockWean.job.number}
              description={data.livestockWean.job.description}
            />
            <InventoryField job={data.livestockWean.job} />
            <FormField
              name="event"
              rules={{
                required: "The event field is required."
              }}
            >
              <FormFieldLabel>Event</FormFieldLabel>
              <FormFieldInput>
                <TypeaheadInput
                  items={data.livestockWeanEventTypes.map(event => ({
                    value: event.code,
                    title: event.description
                  }))}
                />
              </FormFieldInput>
              <FormFieldErrors />
            </FormField>
            <FormField
              name="postingDate"
              rules={{
                required: "The date field is required."
              }}
            >
              <FormFieldLabel>Activity Date</FormFieldLabel>
              <FormFieldInput>
                <DateInput required={true} />
              </FormFieldInput>
              <FormFieldErrors />
            </FormField>
            <QuantityAndSmallsField
              name="smallLivestockQuantity"
              label="Smalls"
            />
            <TotalWeightField />
            <CommentsField />
            <div className="flex">
              <Button className="w-full" type="button" onClick={onSave}>
                Save
              </Button>
              <HorizontalSpacer />
              <FormSubmit />
            </div>
          </Form>
        )}
      </ViewContent>
    </View>
  );
};

export default ActivityWeanView;
